import { uuid } from '@/services/uuid'
import { createApp, defineAsyncComponent, defineComponent, h, onMounted, ref, shallowRef, watchEffect } from 'vue'

import Modal from '@/components/modal/Modal.vue'

import type { App, AsyncComponentLoader, Component, ComponentPublicInstance, ShallowRef } from 'vue'

import '@/components/modal/polyfill'
export interface Dialog {
  createInstance: (binding: Component, attrs: DialogProps & Record<string, unknown>) => void
  showModal<T extends Component>(el: AsyncComponentLoader<T>, props?: DynamicDialogOpenProps<T>): void
  closeModal: () => void
}

export interface DialogProps {
  header?: string
  outside?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
}

type VueComponentProps<T extends Component> = T extends abstract new (...args: unknown[]) => { $props: infer P } ? P : Record<string, unknown>

type DynamicDialogOpenProps<T extends Component> = DialogProps & Record<string, unknown> & Partial<VueComponentProps<T>>

export type DynamicDialogResult<T extends Component> = {
  /** Attr-forwarding wrapper; only created when accessed. */
  readonly component: Component
  open: (props?: DynamicDialogOpenProps<T>) => void
  close: () => void
}

const dialogs = shallowRef<App<Element>[]>([])
const dialogContainers = shallowRef<HTMLDialogElement[]>([])
const dialogInstances = shallowRef<ComponentPublicInstance[]>([])

function openDialog(): void {
  const dialogContainer = dialogContainers.value.pop()
  if (dialogContainer && document.body.contains(dialogContainer)) {
    dialogContainer.showModal()
  } else {
    console.error('Dialog container is not in the document.')
  }
}

export function closeDialog(): void {
  const dialogContainer = dialogContainers.value.pop()

  if (dialogContainer) {
    dialogContainer.close()
    setTimeout(() => dialogContainer.remove(), 100)

    dialogs.value.pop()
    dialogInstances.value.pop()
  }
}

function appendDialog(bindAttrs: ShallowRef<(DialogProps & Record<string, unknown>) | null>): HTMLDialogElement {
  const id = uuid.value

  const dialogContainer = document.createElement('dialog')

  dialogContainer.id = id
  dialogContainer.classList.add('dialog')
  dialogContainer.tabIndex = -1

  if (bindAttrs.value?.class) {
    if (bindAttrs.value.class.toString().split(' ').length > 1) {
      bindAttrs.value.class
        .toString()
        .split(' ')
        .forEach((className: string) => {
          dialogContainer.classList.add(className)
        })
    } else {
      dialogContainer.classList.add(bindAttrs.value.class.toString())
    }
  }

  dialogContainer.classList.add(`dialog-${bindAttrs.value?.size ?? 'md'}`)

  document.body.appendChild(dialogContainer)
  dialogContainers.value.push(dialogContainer)
  return dialogContainer
}

function setupDialog() {
  const bindContainer = shallowRef<Component | null>(null)
  const bindAttrs = shallowRef<(DialogProps & Record<string, unknown>) | null>(null)

  function initModal(): void {
    const dialogContainer = appendDialog(bindAttrs)

    if (bindAttrs.value?.outside !== false) {
      dialogContainer.addEventListener('click', closeDialog)
    }

    const DialogProps: Record<string, unknown> = { ...bindAttrs.value }

    const modal = createApp({
      render: () => h(Modal, DialogProps, { default: () => (bindContainer.value ? h(bindContainer.value, DialogProps) : null) })
    })

    dialogs.value.push(modal)

    dialogInstances.value.push(modal.mount(dialogContainer))
    dialogContainers.value.push(dialogContainer)
  }

  async function createInstance(binding: Component, attrs: DialogProps & Record<string, unknown>): Promise<void> {
    bindContainer.value = binding
    bindAttrs.value = attrs
  }

  return {
    initModal,
    createInstance
  }
}

export function dialog(): Dialog
export function dialog<T extends Component>(el: AsyncComponentLoader<T>): DynamicDialogResult<T>
export function dialog<T extends Component>(el?: AsyncComponentLoader<T>): Dialog | DynamicDialogResult<T> {
  const { initModal, createInstance } = setupDialog()

  function showModal<U extends Component>(componentEl: AsyncComponentLoader<U>, props?: DynamicDialogOpenProps<U>): void {
    const component = defineAsyncComponent(componentEl)
    createInstance(component, props || {})

    initModal()
    openDialog()
  }

  function closeModal(): void {
    closeDialog()
  }

  if (el) {
    const component = defineAsyncComponent(el)
    const latestAttrs = shallowRef<Record<string, unknown>>({})
    let wrapperComponent: Component | undefined

    function open(props: DynamicDialogOpenProps<T> = {}): void {
      const merged: Record<string, unknown> = {
        ...latestAttrs.value,
        ...props
      }
      createInstance(component, merged)
      initModal()
      openDialog()
    }

    function getWrapperComponent(): Component {
      if (wrapperComponent) {
        return wrapperComponent
      }

      const elementUuid = ref<string>('')

      wrapperComponent = defineComponent({
        setup(_props, { attrs }) {
          watchEffect(() => {
            latestAttrs.value = { ...attrs }
          })

          onMounted(() => {
            elementUuid.value = uuid.value
          })

          return () => h('div', { id: elementUuid.value })
        }
      })

      return wrapperComponent
    }

    return {
      get component(): Component {
        return getWrapperComponent()
      },
      open,
      close: closeModal
    }
  }

  return {
    createInstance,
    showModal,
    closeModal
  }
}
